import {
  getCodes,
  getCurriculums,
  getGroups,
  orEmptyArray,
} from '@lms/api-client/backend';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@lms/ui/card';
import { LoaderCircle } from 'lucide-react';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import AddCodeDialog from '@/features/codes/components/add-code';
import { CodeList, EmptyCodeList } from '@/features/codes/components/code-list';
import { NotFound } from '@/routes/not-found';

// Ports app/[brand]/admin/codes/page.tsx. The Next page fetched groups,
// curriculums and codes server-side; here they load via SWR and mutations
// revalidate the codes list in place of `revalidatePath`. Managing codes needs
// `canManageGroups`, so the page is denied to viewers without it.
export function Codes() {
  const t = useTranslations('admin');
  const { brand: site, slug } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);

  const { data: groups } = useSWR(['admin/groups', site.id], () =>
    orEmptyArray(getGroups(site.id)),
  );
  const { data: curriculums } = useSWR(['admin/curriculums', site.id], () =>
    orEmptyArray(getCurriculums(site.id)),
  );
  const { data: codes, mutate: mutateCodes } = useSWR(
    ['admin/codes', site.id],
    () => orEmptyArray(getCodes(site.id)),
  );

  if (roleLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <LoaderCircle className="text-primary size-12 animate-spin" />
      </div>
    );
  }

  if (!access?.canManageGroups) {
    return <NotFound />;
  }

  if (
    groups === undefined ||
    curriculums === undefined ||
    codes === undefined
  ) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('codes')}</PageTitle>
        <PageButtons>
          <AddCodeDialog
            site={site}
            curriculums={curriculums}
            groups={groups}
            onCreated={() => mutateCodes()}
          />
        </PageButtons>
      </PageHeader>
      {groups.length === 0 && <EmptyCodeList groupAlias={site.groupAlias} />}
      <Card>
        <CardHeader>
          <CardTitle>{t('codes')}</CardTitle>
          <CardDescription>
            {t('manage', { what: t('codes').toLowerCase() })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CodeList
            slug={slug}
            groupAlias={site.groupAlias}
            hasGroups={site.hasGroups}
            codes={codes}
            onChanged={() => mutateCodes()}
          />
        </CardContent>
      </Card>
    </PageBox>
  );
}
